/**
 * 2.0 wave 2, W2-CHROME (spec 7.5, CH-4) — ONE deterministic tab set per view.
 *
 * The tab bar used to be derived from role booleans in a fixed precedence
 * (guardian-only first, then coach-only, else athlete), while the Settings body
 * picked coach first (B392): a coach-only plus guardian-only account saw one
 * bar and the other account's Settings. With the View switch the tab set is a
 * pure function of the chosen view, so the bar, the desktop rail and Settings
 * cannot disagree.
 *
 * Pure data. The glyphs and the active-route matchers stay in
 * `components/SideNav/BottomTab.tsx`, which joins them to these specs by `key`;
 * `BottomTab.test.tsx`-level contract tests render the real bar for each view
 * and assert it equals `tabsForView`.
 *
 *  · Training: Today, Train, Run (native) or Runs (web), Races, Team, and
 *    Settings on the web only. On the native app Settings is reached from the
 *    avatar menu, so there is no sixth tab (the native five-tab rule).
 *  · Coaching: Team, Meets, Settings.
 *  · Guardian: Home, Approvals, Settings.
 */

import type { AccountViewId } from '../accountView';

export type TabKey =
  | 'today'
  | 'train'
  | 'run'
  | 'races'
  | 'team'
  | 'settings'
  | 'meets'
  | 'guardian-home'
  | 'approvals';

export interface TabSpec {
  key: TabKey;
  label: string;
  to: string;
}

const TODAY: TabSpec = { key: 'today', label: 'Today', to: '/today' };
const TRAIN: TabSpec = { key: 'train', label: 'Train', to: '/plan' };
const RACES: TabSpec = { key: 'races', label: 'Races', to: '/races' };
const TEAM: TabSpec = { key: 'team', label: 'Team', to: '/team' };
const SETTINGS: TabSpec = { key: 'settings', label: 'Settings', to: '/settings' };

/** Fresh objects every call, so a caller can never mutate the shared specs. */
const fresh = (tabs: TabSpec[]): TabSpec[] => tabs.map((t) => ({ ...t }));

/** The tabs of a view, in bar order. `native` is the Capacitor shell. */
export function tabsForView(view: AccountViewId, native: boolean): TabSpec[] {
  switch (view) {
    case 'guardian':
      return fresh([
        { key: 'guardian-home', label: 'Home', to: '/guardian' },
        { key: 'approvals', label: 'Approvals', to: '/approvals' },
        SETTINGS,
      ]);
    case 'coaching':
      return fresh([TEAM, { key: 'meets', label: 'Meets', to: '/races' }, SETTINGS]);
    case 'training':
    default: {
      const run: TabSpec = { key: 'run', label: native ? 'Run' : 'Runs', to: '/run' };
      return fresh([TODAY, TRAIN, run, RACES, TEAM, ...(native ? [] : [SETTINGS])]);
    }
  }
}

/**
 * True when Settings is a tab of this view's bar. False only for Training on
 * the native app; there Settings is a screen below the avatar menu, so it
 * shows a back link (SS-6) and a tab never does.
 */
export function tabSetHasSettings(view: AccountViewId, native: boolean): boolean {
  return tabsForView(view, native).some((t) => t.key === 'settings');
}

/** The first tab of a view: where choosing the view lands (CH-3). */
export function landingPathForView(view: AccountViewId): string {
  switch (view) {
    case 'coaching':
      return '/team';
    case 'guardian':
      return '/guardian';
    case 'training':
    default:
      return '/today';
  }
}
